import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Chip,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';

const TenantSelector: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant, availableTenants, setCurrentTenant, loading, error } = useTenant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Only show for admin users
  if (!user || user.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading tenants...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ py: 0 }}>
        {error}
      </Alert>
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantSelect = (tenant: typeof currentTenant) => {
    setCurrentTenant(tenant);
    handleClose();
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="h6" color="white">
        Tenant: 
      </Typography>
      <Button
        variant="text"
        size="small"
        startIcon={<BusinessIcon />}
        endIcon={<ExpandMoreIcon />}
        onClick={handleClick}
        sx={{
          textTransform: 'none',
          minWidth: '150px',
          justifyContent: 'space-between',
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Typography variant="body2" noWrap>
          {currentTenant ? currentTenant.name : 'No tenant selected'}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 250 },
        }}
      >
        {availableTenants.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No tenants available" />
          </MenuItem>
        ) : (
          availableTenants.map((tenant) => (
            <MenuItem
              key={tenant.id}
              onClick={() => handleTenantSelect(tenant)}
              selected={currentTenant?.id === tenant.id}
            >
              <ListItemIcon>
                {currentTenant?.id === tenant.id ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <BusinessIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={tenant.name}
                secondary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" component="span">
                      {tenant.slug}
                    </Typography>
                    <Chip
                      label={tenant.is_active ? 'Active' : 'Inactive'}
                      color={tenant.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                }
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

export default TenantSelector;